// SettlementHistoryTimeline.jsx — 정산 설정 이력 타임라인 UI
//
// 가로 스크롤 타임라인:
//   ──●───────────────────●──────────────+──▶
//   [카드: 2020-02-14]  [카드: 2026-04-01 ✓]  [+ 추가]
//
// 각 카드: 날짜 + 설정값 표시, 수정/삭제 버튼
// "+" 클릭: 날짜 입력 → 이전 값으로 초기화된 카드 편집 모드로 추가
// 카드 컴포넌트(HistoryCard/PendingCard)는 SettlementHistoryCards.jsx에서 관리
import React, { useState } from 'react';
import { HistoryCard, PendingCard } from './SettlementHistoryCards';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { todayKST } from '../../../utils/date';
import { stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn } from '../adminlayout/designStandard';

// ── 타임라인 레이아웃 스타일 ──────────────────────────────────────────────────
const S = {
  wrap: {
    overflowX: 'auto',
    padding: '20px 16px 16px',
    background: 'var(--bg-subtle)',
    borderRadius: 10,
  },
  inner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0,
    minWidth: 'max-content',
    position: 'relative',
  },
  // 카드 상단 중앙을 잇는 가로 연결선
  line: {
    position: 'absolute',
    top: 20,
    left: 0,
    right: 0,
    height: 2,
    background: 'var(--border)',
    zIndex: 0,
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
    width: 250,
    marginRight: 24,
  },
  dot: (isCurrent, isActive) => ({
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: `2px solid ${isCurrent ? 'var(--std-accent, #1d7de6)' : isActive ? '#22c55e' : 'var(--border)'}`,
    background: isCurrent ? 'var(--std-accent, #1d7de6)' : isActive ? '#22c55e' : 'var(--bg-card)',
    flexShrink: 0,
    marginBottom: 8,
    boxShadow: 'none',
  }),
  addCol: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    position: 'relative', zIndex: 1, width: 80,
  },
  addDot: {
    width: 28, height: 28, borderRadius: '50%',
    border: '2px dashed var(--border)',
    background: 'var(--bg-card)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', fontSize: 18, color: 'var(--text-muted)',
    transition: 'all 0.15s', flexShrink: 0,
  },
  btn: (variant) => ({
    fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 6,
    border: 'none', cursor: 'pointer',
    background: variant === 'primary' ? 'var(--std-btn, #0f172a)' : 'var(--bg-subtle)',
    color: variant === 'primary' ? '#fff' : 'var(--text-muted)',
  }),
};

// ── 날짜 추가 모달 ─────────────────────────────────────────────────────────────
function AddDateModal({ existingDates, onConfirm, onCancel }) {
  useBodyScrollLock(true);
  const todayStr = todayKST(); // KST 기준 오늘 날짜 (기본값)
  const [date, setDate]   = useState(todayStr);
  const [error, setError] = useState('');

  function handleConfirm() {
    if (!date) { setError('날짜를 선택해주세요.'); return; }
    if (existingDates.includes(date)) { setError('이미 존재하는 날짜입니다.'); return; }
    onConfirm(date);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: 12, minWidth: 260, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
        <div style={stdModalHeaderBar(12)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>📅</div>
            <div style={stdModalHeaderTitle}>이력 추가</div>
          </div>
          <button onClick={onCancel} style={stdModalCloseBtn}>✕</button>
        </div>
        <div style={{ padding: '16px 24px 20px' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            이 날짜부터 적용될 새 설정을 추가합니다.
          </div>
          {/* min을 초기값(2020-02-14) 다음 날로 제한 */}
          <input
            type="date"
            min="2020-02-15"
            value={date}
            onChange={(e) => { setDate(e.target.value); setError(''); }}
            style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 7, padding: '5px 8px', fontSize: 13, color: 'var(--text)', background: 'var(--bg-card)', outline: 'none', boxSizing: 'border-box' }}
          />
          {error && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
            <button style={S.btn('default')} onClick={onCancel}>취소</button>
            <button style={S.btn('primary')} onClick={handleConfirm}>추가</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 메인 타임라인 컴포넌트 ─────────────────────────────────────────────────────
export function SettlementHistoryTimeline({ companyCode, historyData, saving, onSave, onDelete }) {
  const [showAddModal, setShowAddModal] = useState(false);
  // 추가 중인 신규 항목 (서버 저장 전 로컬 상태)
  const [pendingNew,   setPendingNew]   = useState(null); // { effectiveFrom, settings }

  if (!historyData) {
    return <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 12 }}>이력을 불러오는 중...</div>;
  }

  const { products, history } = historyData;
  const existingDates = history.map((h) => h.effectiveFrom);

  // 이전 설정값 가져오기 (새 날짜 추가 시 초기값으로 사용)
  function getPrevSettings() {
    const active = [...history].reverse().find((h) => h.isActive);
    return active?.settings || {};
  }

  function handleAddConfirm(date) {
    setShowAddModal(false);
    setPendingNew({
      effectiveFrom: date,
      isActive: date <= todayKST(),
      isCurrent: false,
      settings: getPrevSettings(),
      isPending: true,
    });
  }

  async function handlePendingSave(effectiveFrom, fields) {
    const ok = await onSave(effectiveFrom, fields);
    if (ok) setPendingNew(null);
    return ok;
  }

  // 저장된 이력 + 추가 중인 임시 항목 합산, 날짜순 정렬
  const allItems = pendingNew
    ? [...history, pendingNew].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))
    : history;

  return (
    <div style={S.wrap}>
      <div style={S.inner}>
        {/* 가로 연결선 */}
        <div style={S.line} />

        {/* 이력 카드 목록 */}
        {allItems.map((item) => (
          <div key={item.effectiveFrom} style={S.col}>
            <div style={S.dot(item.isCurrent, item.isActive)} />
            {item.isPending ? (
              <PendingCard
                effectiveFrom={item.effectiveFrom}
                initSettings={item.settings}
                products={products}
                saving={saving}
                onSave={handlePendingSave}
                onCancel={() => setPendingNew(null)}
              />
            ) : (
              <HistoryCard
                item={item}
                products={products}
                saving={saving}
                onSave={(date, fields) => onSave(date, fields)}
                onDelete={(date) => onDelete(date)}
              />
            )}
          </div>
        ))}

        {/* + 추가 버튼 열 */}
        <div style={S.addCol}>
          <div style={S.addDot} onClick={() => setShowAddModal(true)} title="새 날짜 이력 추가">+</div>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4, whiteSpace: 'nowrap' }}>추가</span>
        </div>
      </div>

      {showAddModal && (
        <AddDateModal
          existingDates={existingDates}
          onConfirm={handleAddConfirm}
          onCancel={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}
